import React, { useState, useRef, useCallback, useEffect } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import TextInput from "../ui/TextInput";
import TextTab from "../../pages/resources/brandVoices/TextTab";
import FilesTab from "../../pages/resources/brandVoices/FilesTab";
import BrandVoiceContentList, { ContentItem } from "../../pages/resources/brandVoices/BrandVoiceContentList";
import Button from "../ui/Button";
import LoadingSpinner from "../ui/LoadingSpinner";
import { createBrandVoice, CreateBrandVoiceRequest } from "../../api/brandVoice";
import { uploadFileToS3Complete } from "../../api/s3";
import { useNotification } from "../../context/NotificationContext";
import styles from "./CreateBrandVoiceModal.module.css";

type TabKey = "text" | "urls" | "files";

interface CreateBrandVoiceModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: (brandVoiceId: string) => void;
}

const CreateBrandVoiceModal: React.FC<CreateBrandVoiceModalProps> = ({ isOpen, onClose, onSuccess }) => {
    const { t: rawT, i18n } = useTranslation();
    const t = rawT as any;
    const { showNotification } = useNotification();
    const modalContentRef = useRef<HTMLDivElement>(null);

    // Get language code from i18n (en or fr)
    const getLanguageCode = (i18nLanguage: string): "en" | "fr" => {
        const lang = i18nLanguage.toLowerCase().split("-")[0];
        return lang === "fr" ? "fr" : "en";
    };

    const [activeTab, setActiveTab] = useState<TabKey>("text");
    const [styleName, setStyleName] = useState("");
    const [contentItems, setContentItems] = useState<ContentItem[]>([]);
    const [isCreating, setIsCreating] = useState(false);

    useEffect(() => {
        if (!isOpen) {
            // Reset form when modal closes
            setStyleName("");
            setContentItems([]);
            setActiveTab("text");
        }
    }, [isOpen]);

    const handleOutsideClick = useCallback(
        (e: MouseEvent) => {
            if (modalContentRef.current && !modalContentRef.current.contains(e.target as Node)) {
                onClose();
            }
        },
        [onClose],
    );

    useEffect(() => {
        if (isOpen) {
            document.addEventListener("mousedown", handleOutsideClick);
            document.body.style.overflow = "hidden";
        }
        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
            document.body.style.overflow = "unset";
        };
    }, [isOpen, handleOutsideClick]);

    const generateId = () => {
        return `item-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    };

    const handleAddText = (payload: { name: string; text: string }) => {
        const textItemsCount = contentItems.filter((item) => item.type === "text").length;
        if (textItemsCount >= 3) {
            showNotification({
                message: t("brandVoices.maxTextExtractsReached") || "Maximum of 3 text extracts reached",
                type: "error",
            });
            return;
        }
        const newItem: ContentItem = {
            id: generateId(),
            type: "text",
            name: payload.name,
            content: payload.text,
        };
        setContentItems((prev) => [...prev, newItem]);
    };

    // const handleAddUrl = (url: string) => {
    //     const urlItemsCount = contentItems.filter((item) => item.type === "url").length;
    //     if (urlItemsCount >= 3) {
    //         showNotification({
    //             message: t("brandVoices.maxUrlsReached") || "Maximum of 3 URLs reached",
    //             type: "error",
    //         });
    //         return;
    //     }
    //     const newItem: ContentItem = {
    //         id: generateId(),
    //         type: "url",
    //         name: url,
    //         url: url,
    //     };
    //     setContentItems((prev) => [...prev, newItem]);
    // };

    const handleAddFiles = (files: File[]) => {
        let currentFileCount = contentItems.filter((item) => item.type === "file").length;
        const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB in bytes

        files.forEach((file) => {
            if (file.size > MAX_FILE_SIZE) {
                showNotification({
                    message:
                        t("brandVoices.fileTooLarge", { fileName: file.name }) ||
                        `File ${file.name} exceeds maximum size of 20MB`,
                    type: "error",
                });
                return;
            }

            if (currentFileCount >= 3) {
                showNotification({
                    message: t("brandVoices.maxFilesReached") || "Maximum of 3 files reached",
                    type: "error",
                });
                return;
            }

            const newItem: ContentItem = {
                id: generateId(),
                type: "file",
                name: file.name,
                file: file,
            };
            setContentItems((prev) => [...prev, newItem]);
            currentFileCount++;
        });
    };

    const handleAddDatabaseReference = (fileId: string, fileName: string) => {
        const fileItemsCount = contentItems.filter((item) => item.type === "file").length;
        if (fileItemsCount >= 3) {
            showNotification({
                message: t("brandVoices.maxFilesReached") || "Maximum of 3 files reached",
                type: "error",
            });
            return;
        }
        const newItem: ContentItem = {
            id: generateId(),
            type: "file",
            name: fileName,
            file: { id: fileId, name: fileName },
        };
        setContentItems((prev) => [...prev, newItem]);
    };

    const handleRemoveItem = (id: string) => {
        setContentItems((prev) => prev.filter((item) => item.id !== id));
    };

    const handleCreateBrandVoice = async () => {
        // Validation
        if (!styleName.trim()) {
            showNotification({
                message: t("brandVoices.missingName"),
                type: "error",
            });
            return;
        }

        if (contentItems.length === 0) {
            showNotification({
                message: t("brandVoices.noContentAdded") || "Please add at least one content item",
                type: "error",
            });
            return;
        }

        setIsCreating(true);

        try {
            const token = localStorage.getItem("token");
            if (!token) {
                throw new Error("Authentication token not found");
            }

            // Transform content items to API format
            const requestData: CreateBrandVoiceRequest = {
                name: styleName.trim(),
                language: getLanguageCode(i18n.language), // Required: use i18n language (en or fr)
            };

            // Collect text items
            const textItems = contentItems.filter((item) => item.type === "text");
            if (textItems.length > 0) {
                requestData.text = textItems.map((item) => item.content || "").join("\n\n");
            }

            // Collect URL items (concatenate as text for now)
            // Note: In a production app, you'd want to extract text from URLs
            const urlItems = contentItems.filter((item) => item.type === "url");
            if (urlItems.length > 0) {
                const urlTexts = urlItems.map((item) => item.url || "").join("\n");
                // Append to text or use as websiteText if text is empty
                if (requestData.text) {
                    requestData.text += `\n\nURLs:\n${urlTexts}`;
                } else {
                    requestData.websiteText = urlTexts;
                }
            }

            // Collect file items
            const fileItems = contentItems.filter((item) => item.type === "file");
            const fileUrls: string[] = [];
            const companyFileIds: string[] = [];

            for (const item of fileItems) {
                if (item.file) {
                    // Check if it's a database file (has id property) or a new file upload
                    if (typeof item.file === "object" && "id" in item.file) {
                        // Database file - use companyFileIds
                        companyFileIds.push(item.file.id);
                    } else if (item.file instanceof File) {
                        // New file upload - upload to S3 first
                        try {
                            const uploadedUrl = await uploadFileToS3Complete(
                                {
                                    file: item.file,
                                    bucket: "custom-style",
                                    isPublic: false,
                                    isFolder: true,
                                },
                                token,
                            );
                            fileUrls.push(uploadedUrl);
                        } catch (uploadError) {
                            console.error("Error uploading file:", uploadError);
                            showNotification({
                                message: t("brandVoices.fileUploadError") || `Failed to upload file: ${item.file.name}`,
                                type: "error",
                            });
                            setIsCreating(false);
                            return;
                        }
                    }
                }
            }

            if (fileUrls.length > 0) {
                requestData.fileUrls = fileUrls;
            }
            if (companyFileIds.length > 0) {
                requestData.companyFileIds = companyFileIds;
            }

            // Validate that at least one source is provided
            if (
                !requestData.text &&
                !requestData.websiteText &&
                !requestData.fileUrls?.length &&
                !requestData.companyFileIds?.length
            ) {
                showNotification({
                    message: t("brandVoices.noContentSource") || "Please provide at least one content source",
                    type: "error",
                });
                setIsCreating(false);
                return;
            }

            // Call the API
            const result = await createBrandVoice(requestData);

            showNotification({
                message: t("brandVoices.createdSuccessfully") || "Brand voice created successfully!",
                type: "success",
            });

            // Call success callback with the new brand voice ID
            onSuccess(result.id);
            onClose();
        } catch (error: any) {
            console.error("Error creating brand voice:", error);
            let errorMessage = t("brandVoices.createError") || "Failed to create brand voice";

            if (error.message) {
                if (error.message.includes("400")) {
                    errorMessage = t("brandVoices.maxReached") || "Maximum of 10 brand voices reached";
                } else if (error.message.includes("401")) {
                    errorMessage = t("brandVoices.unauthorized") || "Unauthorized. Please log in again.";
                } else {
                    errorMessage = error.message;
                }
            }

            showNotification({
                message: errorMessage,
                type: "error",
            });
        } finally {
            setIsCreating(false);
        }
    };

    const renderTabContent = () => {
        switch (activeTab) {
            // case "urls":
            //     return (
            //         <UrlsTab
            //             name={styleName}
            //             onAddUrl={(url) => {
            //                 handleAddUrl(url);
            //             }}
            //             urlItemsCount={contentItems.filter((item) => item.type === "url").length}
            //         />
            //     );
            case "files":
                return (
                    <FilesTab
                        name={styleName}
                        onAddDatabaseReference={(fileId: string, fileName: string) => {
                            handleAddDatabaseReference(fileId, fileName);
                        }}
                        onDropFiles={(files) => {
                            handleAddFiles(files);
                        }}
                        fileItemsCount={contentItems.filter((item) => item.type === "file").length}
                        onMultipleFilesError={(message: string) => {
                            showNotification({
                                message,
                                type: "error",
                            });
                        }}
                    />
                );
            case "text":
            default:
                return (
                    <TextTab
                        name={styleName}
                        onAddText={(payload) => {
                            handleAddText(payload);
                        }}
                        textItemsCount={contentItems.filter((item) => item.type === "text").length}
                    />
                );
        }
    };

    const canCreate = styleName.trim().length > 0 && contentItems.length > 0 && !isCreating;

    if (!isOpen) return null;

    return ReactDOM.createPortal(
        <div className={styles.modalOverlay}>
            <div className={styles.modalContent} ref={modalContentRef}>
                <div className={styles.modalHeader}>
                    <h2 className={styles.modalTitle}>{t("brandVoices.createTitle") || "Create Brand Voice"}</h2>
                    <button className={styles.closeButton} onClick={onClose}>
                        ×
                    </button>
                </div>

                <div className={styles.modalBody}>
                    <div className={styles.section}>
                        <label className={styles.label}>{t("brandVoices.nameLabel")}</label>
                        <p className={styles.sectionDescription}>{t("brandVoices.nameDescription")}</p>
                        <TextInput
                            placeholder={t("name")}
                            value={styleName}
                            onChange={(e) => setStyleName(e.target.value)}
                        />
                    </div>

                    <div className={styles.section}>
                        <h2 className={styles.sectionTitle}>{t("brandVoices.examplesTitle")}</h2>
                        <p className={styles.sectionDescription}>{t("brandVoices.examplesDescription")}</p>
                    </div>

                    <div className={styles.tabsCard}>
                        <div className={styles.tabsWrapper}>
                            {(["text", "files"] as TabKey[]).map(
                                (
                                    tab, // urls are removed for now
                                ) => (
                                    <button
                                        key={tab}
                                        type="button"
                                        onClick={() => setActiveTab(tab)}
                                        className={`${styles.tabButton} ${activeTab === tab ? styles.tabButtonActive : ""}`}
                                    >
                                        {t(`brandVoices.tabs.${tab}`)}
                                    </button>
                                ),
                            )}
                        </div>
                        <div className={styles.instructions}>{t(`brandVoices.instructions.${activeTab}`)}</div>
                        {renderTabContent()}
                    </div>

                    <BrandVoiceContentList items={contentItems} onRemoveItem={handleRemoveItem} />
                </div>

                <div className={styles.modalFooter}>
                    {isCreating ? (
                        <LoadingSpinner />
                    ) : (
                        <>
                            <Button variant="cancel" label={t("cancel")} onClick={onClose} />
                            <Button
                                variant="primary"
                                label={t("brandVoices.createBrandVoice") || "Create Brand Voice"}
                                onClick={handleCreateBrandVoice}
                                disabled={!canCreate}
                            />
                        </>
                    )}
                </div>
            </div>
        </div>,
        document.body,
    );
};

export default CreateBrandVoiceModal;
